import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom'; // Added Link
import { UserCheck, ArrowRight, ArrowLeft } from 'lucide-react';
import logo from '../assets/logo.png';
import { motion } from 'framer-motion';

export default function Login({ title = "Faculty Login", subtitle = "Secure access for staff.", type = "faculty" }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const { login } = useAuth();
    const navigate = useNavigate();
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Dynamic Assets based on Type
    const isFaculty = type === 'faculty';

    // Using Neutral Blue/Teal Illustrations to match brand
    const illustration = isFaculty
        ? "https://cdn-icons-png.flaticon.com/512/747/747376.png"
        : "https://cdn-icons-png.flaticon.com/512/2329/2329087.png"
        ;

    // Exact Brand Colors
    const brandTeal = '#319795';
    const brandNavy = '#1A202C';

    const primaryColor = isFaculty ? brandTeal : brandNavy;
    const rightSideGradient = isFaculty
        ? 'linear-gradient(135deg, #319795 0%, #2C7A7B 100%)'
        : 'linear-gradient(135deg, #2D3748 0%, #1A202C 100%)';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            // Trim inputs to avoid accidental spaces causing validation errors
            const res = await login(email.trim(), password.trim());
            if (res.user && (res.user.role === 'admin' || res.user.role === 'sub_admin')) {
                navigate('/admin/dashboard');
            } else if (res.user && res.user.role === 'hardware_engineer') {
                navigate('/engineer/dashboard');
            } else if (res.user && res.user.role === 'auto_driver') {
                navigate('/auto-driver/dashboard');
            } else {
                navigate('/dashboard');
            }
        } catch (e) {
            console.error(e);

            // Handle Validation Errors (422) specifically
            if (e.response && e.response.status === 422 && e.response.data.errors) {
                // Get the first error message from the validation errors object
                const firstErrorValues = Object.values(e.response.data.errors)[0];
                const firstErrorMessage = Array.isArray(firstErrorValues) ? firstErrorValues[0] : firstErrorValues;
                setError(firstErrorMessage);
            }
            // Handle standard error messages
            else if (e.response && e.response.data && e.response.data.message) {
                setError(`Error: ${e.response.data.message}`);
            } else if (e.message) {
                setError(`System Error: ${e.message}`);
            } else {
                setError('Invalid credentials');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: '#F0F4F8',
            fontFamily: "var(--font-family-base)",
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Navbar (Copied from Home.jsx for Consistency) */}
            <nav style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0 8%',
                position: 'fixed',
                width: '100%',
                top: 0,
                zIndex: 1000,
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(12px)',
                borderBottom: '1px solid #E2E8F0',
                height: '60px',
                boxSizing: 'border-box'
            }}>
                <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                    <img src={logo} alt="Logo" style={{ height: '28px' }} />
                    <div style={{ lineHeight: '1' }}>
                        <span style={{ display: 'block', fontWeight: '900', fontSize: '1.2rem', color: '#1A202C', letterSpacing: '-0.5px' }}>SECURE</span>
                        <span style={{ display: 'block', fontWeight: '600', fontSize: '0.7rem', color: '#319795', letterSpacing: '2px', textTransform: 'uppercase' }}>INSIGHTS</span>
                    </div>
                </Link>

                <Link to="/" style={{
                    padding: '0.6rem 1.2rem',
                    color: '#4A5568',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'color 0.2s',
                    border: '1px solid #E2E8F0',
                    borderRadius: '4px',
                    background: 'white'
                }}>
                    <ArrowLeft size={16} /> Back to Home
                </Link>
            </nav>

            <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1rem',
                marginTop: '60px' // Offset for fixed header
            }}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="login-card"
                    style={{
                        width: '100%',
                        maxWidth: '850px',
                        height: '500px',
                        background: '#ffffff',
                        borderRadius: '16px',
                        // Enhanced 4-sided Shadow
                        boxShadow: '0 0 60px rgba(0, 0, 0, 0.08), 0 20px 40px rgba(0,0,0,0.05)',
                        display: 'flex',
                        overflow: 'hidden',
                        border: '1px solid white'
                    }}
                >

                    {/* Left Side: Form */}
                    <div style={{ flex: 1.1, padding: '3rem', display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative' }}>

                        <div style={{ textAlign: 'left', marginBottom: '2rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.8rem' }}>
                                <img src={logo} alt="Logo" style={{ height: '24px' }} />
                                <span style={{ fontWeight: '800', fontSize: '0.9rem', color: brandNavy, letterSpacing: '0.5px' }}>SECURE CMS</span>
                            </div>
                            <h2 style={{ fontSize: '1.8rem', fontWeight: '800', color: brandNavy, marginBottom: '0.4rem', letterSpacing: '-0.5px' }}>{title}</h2>
                            <p style={{ color: '#718096', fontSize: '0.9rem', fontWeight: '500' }}>{subtitle}</p>
                        </div>

                        {error && (
                            <div style={{ background: '#FFF5F5', color: '#E53E3E', padding: '0.6rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '600' }}>
                                <UserCheck size={16} /> {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', color: '#4A5568', marginBottom: '0.5rem', marginLeft: '2px' }}>Email Address</label>
                                <input
                                    type="email"
                                    className="custom-login-input"
                                    placeholder={isFaculty ? "teacher@school.com" : "adminsecure@gmail.com"}
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    required
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', color: '#4A5568', marginBottom: '0.5rem', marginLeft: '2px' }}>Password</label>
                                <input
                                    type="password"
                                    className="custom-login-input"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    required
                                />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem', marginTop: '0.2rem' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#718096', cursor: 'pointer' }}>
                                    <input type="checkbox" style={{ accentColor: primaryColor, width: '16px', height: '16px' }} /> Remember me
                                </label>
                                <a href="#" style={{ color: primaryColor, textDecoration: 'none', fontWeight: '600' }}>Forgot?</a>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="login-btn"
                                style={{
                                    marginTop: '1rem',
                                    height: '48px',
                                    background: primaryColor,
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem',
                                    transition: 'all 0.2s',
                                    boxShadow: `0 4px 14px ${primaryColor}40`
                                }}
                            >
                                {isLoading ? 'Authenticating...' : (isFaculty ? 'Access Dashboard' : 'Admin Login')} <ArrowRight size={20} />
                            </button>
                        </form>
                    </div>

                    {/* Right Side: Visual */}
                    <div
                        className="desktop-only"
                        style={{
                            flex: 1,
                            background: rightSideGradient,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '2rem',
                            color: 'white',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                        {/* Background Patterns */}
                        <div style={{ position: 'absolute', top: '-10%', right: '-10%', width: '200px', height: '200px', borderRadius: '50%', background: 'white', opacity: 0.05 }}></div>
                        <div style={{ position: 'absolute', bottom: '-10%', left: '-10%', width: '150px', height: '150px', borderRadius: '50%', background: 'white', opacity: 0.05 }}></div>

                        <div style={{
                            background: 'rgba(255, 255, 255, 0.15)',
                            padding: '2rem',
                            borderRadius: '50%',
                            marginBottom: '2rem',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                        }}>
                            <img
                                src={illustration}
                                alt="Illustration"
                                style={{ width: '80px', height: '80px', objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
                            />
                        </div>

                        <h3 style={{ fontSize: '1.6rem', fontWeight: '800', marginBottom: '0.8rem', textAlign: 'center' }}>
                            {isFaculty ? 'Faculty Portal' : 'Admin Control'}
                        </h3>
                        <p style={{ fontSize: '0.95rem', opacity: 0.9, textAlign: 'center', maxWidth: '300px', lineHeight: '1.6' }}>
                            {isFaculty ? 'Manage students, attendance, and exams in one place.' : 'Complete oversight of your institute\'s operations.'}
                        </p>
                    </div>
                </motion.div>
                <style>{`
                    @media (max-width: 900px) {
                        .desktop-only { display: none !important; }
                        .login-card { height: auto !important; max-width: 450px !important; }
                        nav { padding: 0 5% !important; }
                    }

                    /* FORCEFUL STYLING WITH !important */
                    .custom-login-input {
                        width: 100% !important;
                        height: 48px !important;
                        padding: 0 1.2rem !important;
                        background: #ffffff !important;
                        border: 1.5px solid #E2E8F0 !important;
                        border-radius: 8px !important;
                        font-size: 0.95rem !important;
                        color: #2D3748 !important;
                        outline: none !important;
                        transition: all 0.2s !important;
                        box-sizing: border-box !important;
                        font-family: 'Poppins', sans-serif !important;
                        font-weight: 500 !important;
                    }
                    
                    .custom-login-input:focus {
                        border-color: ${primaryColor} !important;
                        box-shadow: 0 0 0 3px ${primaryColor}20 !important;
                    }

                    /* Autofill Reset Hack */
                    input.custom-login-input:-webkit-autofill,
                    input.custom-login-input:-webkit-autofill:hover, 
                    input.custom-login-input:-webkit-autofill:focus, 
                    input.custom-login-input:-webkit-autofill:active {
                        -webkit-box-shadow: 0 0 0 30px white inset !important;
                        -webkit-text-fill-color: #2D3748 !important;
                        transition: background-color 5000s ease-in-out 0s !important;
                    }

                    .login-btn:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 6px 20px ${primaryColor}60 !important;
                    }
                `}</style>
            </div>
        </div>
    );
}
